import { useEffect, useRef, useState } from "react";
import {
  completeMultipart,
  finalizeContribution,
  getPartUrl,
  initContribution,
  initMultipart,
  uploadPart,
  uploadToR2,
} from "./api";

// Files larger than this use the resumable multipart path so a
// broken-network retry doesn't restart from scratch.
const MULTIPART_THRESHOLD = 16 * 1024 * 1024;

// Upload UI for contribution-enabled collection shares. Lives above
// the poster grid as a friendly call-to-action. First-time
// contributors are prompted for a display name (persisted in
// localStorage so we don't ask twice on this browser); subsequent
// visits skip straight to the file picker.

const NAME_KEY = "ud:share:contributor-name";

interface PerFileState {
  name: string;
  status: "queued" | "uploading" | "done" | "error";
  error?: string;
}

export function ContributeBar(props: {
  token: string;
  onUploaded: () => void;
}) {
  const [displayName, setDisplayName] = useState<string>(() => {
    try {
      return localStorage.getItem(NAME_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [files, setFiles] = useState<PerFileState[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Anchor for the "uploaded N files" banner.
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    if (!uploading && doneCount > 0) {
      const t = setTimeout(() => {
        setDoneCount(0);
        setFiles([]);
      }, 6_000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [uploading, doneCount]);

  const openPicker = () => {
    if (!displayName) {
      setNamePromptOpen(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const confirmName = () => {
    const n = draftName.trim();
    if (!n) return;
    setDisplayName(n);
    try {
      localStorage.setItem(NAME_KEY, n);
    } catch {
      /* ignore */
    }
    setNamePromptOpen(false);
    setDraftName("");
    // Open the picker immediately so the user doesn't have to click twice.
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const onFiles = async (chosen: File[]) => {
    if (chosen.length === 0) return;
    const initial: PerFileState[] = chosen.map((f) => ({
      name: f.name,
      status: "queued",
    }));
    setFiles(initial);
    setDoneCount(0);
    setUploading(true);

    for (let i = 0; i < chosen.length; i++) {
      const f = chosen[i];
      setFiles((prev) =>
        prev.map((p, idx) => (idx === i ? { ...p, status: "uploading" } : p)),
      );
      try {
        if (f.size > MULTIPART_THRESHOLD) {
          await uploadMultipart(props.token, f, displayName);
        } else {
          await uploadSingle(props.token, f, displayName);
        }
        setFiles((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: "done" } : p)),
        );
        setDoneCount((c) => c + 1);
      } catch (err) {
        const message = (err as Error).message ?? "Upload failed";
        setFiles((prev) =>
          prev.map((p, idx) =>
            idx === i ? { ...p, status: "error", error: message } : p,
          ),
        );
      }
    }
    setUploading(false);
    props.onUploaded();
  };

  return (
    <div className="mb-4">
      <div className="rounded-xl ring-1 ring-zinc-200 dark:ring-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
            Contribute to this album
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
            {displayName
              ? `You'll appear as “${displayName}”. The album owner reviews uploads before they appear.`
              : "Pick the photos or videos you want to share. You'll be asked for a display name once."}
          </p>
        </div>
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading}
          className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-1.5 shrink-0 disabled:opacity-60"
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>

      {files.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center justify-between text-xs px-3 py-1.5 rounded-md bg-zinc-50 dark:bg-zinc-900/60 ring-1 ring-zinc-200 dark:ring-zinc-800"
            >
              <span className="truncate text-zinc-700 dark:text-zinc-300">
                {f.name}
              </span>
              <span
                className={
                  f.status === "done"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : f.status === "error"
                    ? "text-red-600 dark:text-red-400"
                    : "text-zinc-500 dark:text-zinc-400"
                }
              >
                {f.status === "uploading"
                  ? "Uploading…"
                  : f.status === "done"
                  ? "Pending review"
                  : f.status === "error"
                  ? f.error ?? "Failed"
                  : "Queued"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {doneCount > 0 && !uploading ? (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
          {doneCount} file{doneCount === 1 ? "" : "s"} uploaded — the album owner will review and approve them shortly.
        </p>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          // Reset so re-picking the same files fires onChange again.
          e.target.value = "";
          if (list.length > 0) void onFiles(list);
        }}
      />

      {namePromptOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setNamePromptOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-zinc-800 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                What's your name?
              </h2>
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                The album owner sees this next to your uploads. Just a label — no account needed.
              </p>
            </div>
            <div className="px-5 py-4">
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                autoFocus
                placeholder="e.g. Anna"
                className="w-full rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2.5 py-1.5 text-sm"
              />
            </div>
            <div className="px-5 py-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setNamePromptOpen(false)}
                className="rounded-md text-sm px-3 py-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmName}
                disabled={!draftName.trim()}
                className="rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-3 py-1.5 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Single-PUT path — small files. Browser puts the whole blob; init
// + finalize bracket the upload.
async function uploadSingle(
  token: string,
  f: File,
  displayName: string,
): Promise<void> {
  const init = await initContribution(token, {
    name: f.name,
    size: f.size,
    mimeType: f.type || "application/octet-stream",
    contributorName: displayName,
  });
  await uploadToR2(init.uploadUrl, f);
  await finalizeContribution(token, init.sessionId);
}

// Multipart path — big files. Chunks the blob into PART_SIZE pieces,
// PUTs each to its presigned URL, collects ETags, then asks the
// server to assemble them into the final object. Per-part retry
// fetches a fresh presigned URL on first failure.
async function uploadMultipart(
  token: string,
  f: File,
  displayName: string,
): Promise<void> {
  const init = await initMultipart(token, {
    name: f.name,
    size: f.size,
    mimeType: f.type || "application/octet-stream",
    contributorName: displayName,
  });
  const { sessionId, partSize, partCount, partUrls } = init;
  const urlByPart = new Map<number, string>();
  for (const p of partUrls) urlByPart.set(p.partNumber, p.url);

  const contentType = f.type || "application/octet-stream";
  const partsCollected: Array<{ partNumber: number; etag: string }> = [];

  for (let part = 1; part <= partCount; part++) {
    const start = (part - 1) * partSize;
    const end = Math.min(f.size, start + partSize);
    const chunk = f.slice(start, end);
    let url = urlByPart.get(part);
    if (!url) {
      // Init only returns the first 200 part URLs; ask for more as
      // we go.
      const r = await getPartUrl(token, sessionId, part);
      url = r.url;
    }
    const etag = await uploadPart(url, chunk, contentType, async () => {
      // Refresh fetches a fresh presigned URL for this part.
      const r = await getPartUrl(token, sessionId, part);
      return r.url;
    });
    partsCollected.push({ partNumber: part, etag });
  }

  await completeMultipart(token, sessionId, partsCollected);
}
